import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Reservation, ReservationPriceModifier } from '@turizi/shared';
import { useAddPriceModifier, useRemovePriceModifier } from '@/hooks/api-hooks';
import { formatPrice, formatExchangeRate, formatDayLongTime } from '@/lib/format-utils';
import { Button, Modal, Select, TextInput, NumberInput, SegmentedControl, ActionIcon, Loader, Badge } from '@mantine/core';
import { IconPlus, IconTrash, IconReceiptTax, IconPercentage, IconCurrencyDollar, IconCashBanknoteMove } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';

interface Props {
    reservation: Reservation;
    onOpenExchangeModal?: () => void;
    disabled?: boolean;
}

export default function ReservationPriceModifiers({ reservation, onOpenExchangeModal, disabled = false }: Props) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);


    const addPriceModifier = useAddPriceModifier();
    const removePriceModifier = useRemovePriceModifier();

    const [section, setSection] = useState<string>('manual_adjustment');
    const [type, setType] = useState<string>('fixed');
    const [direction, setDirection] = useState<string>('subtract');
    const [amount, setAmount] = useState<number | ''>(0);
    const [currency, setCurrency] = useState<string>('');
    const [description, setDescription] = useState<string>('');

    const modifiers: ReservationPriceModifier[] = reservation?.price_modifiers || [];

    const availableCurrencies = Array.from(new Set([
        ...(reservation?.service_bookings?.map(b => b.money_in_currency).filter(Boolean) as string[] || []),
    ]));

    const handleSectionChange = (val: string | null) => {
        const newSection = val || 'manual_adjustment';
        setSection(newSection);
        if (newSection === 'taxes' || newSection === 'surcharge' || newSection === 'fee') {
            setDirection('add');
        } else if (newSection === 'manual_adjustment' || newSection === 'voucher') {
            setDirection('subtract');
        }
    };

    const handleOpenModal = () => {
        setSection('manual_adjustment');
        setType('fixed');
        setDirection('subtract');
        setAmount(0);
        setCurrency(availableCurrencies[0] || '');
        setDescription('');
        setOpened(true);
    };

    const handleSave = () => {
        const numericAmount = Number(amount || 0);
        if (numericAmount === 0 && type === 'fixed') return;

        const signedValue = direction === 'subtract' ? -Math.abs(numericAmount) : Math.abs(numericAmount);

        addPriceModifier.mutate(
            {
                reservationId: reservation.id,
                data: {
                    section,
                    type,
                    value: signedValue,
                    currency: currency || null,
                    description: description || null
                }
            },
            {
                onSuccess: () => {
                    setOpened(false);
                    notifications.show({
                        title: t('price_modifiers'),
                        message: t('price_modifier_added'),
                        color: 'green'
                    });
                }
            }
        );
    };

    const handleDelete = (modifierId: number) => {
        removePriceModifier.mutate(
            {
                id: modifierId,
                reservationId: reservation.id
            },
            {
                onSuccess: () => {
                    notifications.show({
                        title: t('price_modifiers'),
                        message: t('price_modifier_deleted'),
                        color: 'blue'
                    });
                }
            }
        );
    };

    const getSectionBadgeColor = (sec: string, val: number) => {
        if (sec === 'exchange') {
            return 'cyan';
        }
        if (val < 0 || sec === 'manual_adjustment' || sec === 'voucher') {
            return 'teal';
        }
        if (sec === 'taxes') {
            return 'violet';
        }
        return 'orange';
    };

    return (
        <div className="space-y-2">
            {/* Header */}
            {!disabled && (
                <div className="flex items-center justify-between gap-1.5 flex-wrap sm:flex-nowrap">
                    {(addPriceModifier.isPending || removePriceModifier.isPending) && (
                        <Loader size="xs" color="blue" />
                    )}
                    {onOpenExchangeModal && (
                        <Button
                            size="xs"
                            variant="subtle"
                            color="blue"
                            radius="md"
                            leftSection={<IconCashBanknoteMove size={13} />}
                            onClick={onOpenExchangeModal}
                            className="h-6 px-1.5 text-xs font-medium shrink-0"
                        >
                            {t('currency_exchange')}
                        </Button>
                    )}
                    <Button
                        size="xs"
                        variant="subtle"
                        color="blue"
                        radius="md"
                        leftSection={<IconPlus size={12} />}
                        onClick={handleOpenModal}
                        className="h-6 px-1.5 text-xs font-medium shrink-0"
                    >
                        {t('add_modifier')}
                    </Button>
                </div>
            )}

            {/* List */}
            {modifiers.length === 0 ? (
                null
            ) : (
                <div className="divide-y divide-gray-100">
                    {modifiers.map((mod) => {
                        const val = Number(mod.value || 0);
                        const formattedSign = val > 0 ? '+' : (val < 0 ? '-' : '');
                        const absVal = Math.abs(val);
                        const customDesc = mod.description && mod.description.trim() !== t(mod.section) && mod.description.trim() !== t('currency_exchange');

                        if (mod.type === 'exchange' || mod.section === 'exchange') {
                            const sourceCurr = mod.description ? mod.description.trim().split(/[\s→:]+/)[0]?.toUpperCase() : null;
                            const targetCurr = mod.currency;

                            let origAmount = 0;
                            if (sourceCurr && reservation?.service_bookings) {
                                reservation.service_bookings.forEach(b => {
                                    if (b.money_in_currency === sourceCurr && b.money_in_amount !== null) {
                                        origAmount += Number(b.money_in_amount || 0);
                                    }
                                });
                            }

                            const dateStr = mod.updated_at || mod.created_at;
                            const formattedDate = dateStr ? formatDayLongTime(dateStr) : null;

                            return (
                                <div
                                    key={mod.id}
                                    className="p-2.5 bg-slate-50/90 border border-slate-200 rounded-lg space-y-2 my-2 shadow-2xs transition-colors hover:bg-slate-100/80"
                                >
                                    {/* Row 1: Badge & Delete Button */}
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                                            <Badge
                                                size="xs"
                                                variant="light"
                                                color="cyan"
                                                className="shrink-0 text-[10px] font-bold uppercase tracking-wider"
                                            >
                                                {t('currency_exchange')}
                                            </Badge>
                                            {formattedDate && (
                                                <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">
                                                    {formattedDate}
                                                </span>
                                            )}
                                        </div>

                                        {!disabled && (
                                            <ActionIcon
                                                size="xs"
                                                color="red"
                                                variant="subtle"
                                                loading={removePriceModifier.isPending && removePriceModifier.variables?.id === mod.id}
                                                onClick={() => handleDelete(mod.id)}
                                                title={t('remove_price_modifier')}
                                                className="hover:bg-red-50 shrink-0"
                                            >
                                                <IconTrash size={13} />
                                            </ActionIcon>
                                        )}
                                    </div>

                                    {/* Row 2: Currency Pair & Rate Badge */}
                                    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                                        <span className="font-extrabold text-xs text-slate-800 tracking-tight">
                                            {mod.description || `${sourceCurr} → ${targetCurr}`}
                                        </span>
                                        <span className="text-xs font-bold text-slate-700 bg-white px-2 py-0.5 rounded border border-slate-200/90 shadow-2xs">
                                            {t('exchange_rate')}: <span className="font-black text-blue-700">{formatExchangeRate(val)}</span>
                                        </span>
                                    </div>

                                    {/* Row 3: Original Base Amount */}
                                    <div className="pt-1.5 border-t border-slate-200/60 flex items-center justify-between text-[11px] flex-wrap gap-1">
                                        <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                                            {t('original_amount')}:
                                        </span>
                                        <span className="font-bold text-slate-700">
                                            ${formatPrice(origAmount)} {sourceCurr}
                                        </span>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div
                                key={mod.id}
                                className="py-1.5 flex items-center justify-between gap-2 text-xs min-w-0 hover:bg-slate-50/60 rounded px-1 transition-colors"
                            >
                                <div className="flex items-center gap-1.5 min-w-0">
                                    {mod.section === 'exchange' ? t('currency_exchange') : t(mod.section)}
                                    {customDesc && (
                                        <span className="font-medium text-slate-700 whitespace-nowrap text-xs" title={mod.description || ''}>
                                            {mod.description}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <span className="text-xs font-bold">
                                        {formattedSign} {mod.type === 'percentage' ? '%' : '$'} {formatPrice(absVal)} {mod.currency || '**'}
                                    </span>

                                    {!disabled && (
                                        <ActionIcon
                                            size="xs"
                                            color="red"
                                            variant="subtle"
                                            loading={removePriceModifier.isPending && removePriceModifier.variables?.id === mod.id}
                                            onClick={() => handleDelete(mod.id)}
                                            title={t('remove_price_modifier')}
                                            className="hover:bg-red-50 shrink-0"
                                        >
                                            <IconTrash size={13} />
                                        </ActionIcon>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal */}
            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title={<span className="font-bold text-sm">{t('add_price_modifier')}</span>}
                centered
                size="sm"
            >
                <div className="space-y-3.5 pt-1">
                    <Select
                        label={t('price_modifier_section')}
                        value={section}
                        onChange={handleSectionChange}
                        data={[
                            { value: 'manual_adjustment', label: t('manual_adjustment') },
                            { value: 'taxes', label: t('taxes') },
                            { value: 'voucher', label: t('voucher') },
                            { value: 'surcharge', label: t('surcharge') },
                            { value: 'fee', label: t('fee') },
                        ]}
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-semibold text-gray-700 block mb-1">
                                {t('price_modifier_type')}
                            </label>
                            <SegmentedControl
                                fullWidth
                                size="xs"
                                value={type}
                                onChange={(val: string) => setType(val)}
                                data={[
                                    { label: t('fixed_amount'), value: 'fixed' },
                                    { label: t('percentage'), value: 'percentage' },
                                ]}
                            />
                        </div>

                        <div>
                            <label className="text-xs font-semibold text-gray-700 block mb-1">
                                {t('modifier_operation')}
                            </label>
                            <SegmentedControl
                                fullWidth
                                size="xs"
                                value={direction}
                                onChange={(val: string) => setDirection(val)}
                                data={[
                                    { label: `- (${t('discount')})`, value: 'subtract' },
                                    { label: `+ (${t('surcharge')})`, value: 'add' },
                                ]}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <NumberInput
                            label={t('price_modifier_value')}
                            value={amount}
                            onChange={(val) => setAmount(typeof val === 'number' ? val : '')}
                            min={0}
                            decimalScale={2}
                            leftSection={type === 'percentage' ? <IconPercentage size={14} /> : <IconCurrencyDollar size={14} />}
                        />
                        <Select
                            label={t('price_modifier_currency')}
                            value={currency}
                            onChange={(val) => setCurrency(val || '')}
                            data={availableCurrencies.map(c => ({ value: c, label: c }))}
                        />
                    </div>

                    <TextInput
                        label={t('price_modifier_description')}
                        value={description}
                        onChange={(e) => setDescription(e.currentTarget.value)}
                        placeholder={t('price_modifier_placeholder')}
                    />

                    <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                        <Button variant="default" size="xs" onClick={() => setOpened(false)}>
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            loading={addPriceModifier.isPending}
                            onClick={handleSave}
                        >
                            {t('add_modifier')}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
